function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import React, { useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import { EMPTY_FUNCTION } from '@deephaven/utils';
import { Item, Picker } from '@deephaven/components';
import { SELECTION_DIRECTION } from '@deephaven/grid';
import "./CellDropdownField.css";
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * A dropdown cell editor for use in an editable grid cell.
 * Renders a Picker component in place of the standard textarea used by CellInputField.
 * Intended to be supplied as the `renderCellInputComponent` prop on Grid when the
 * column restriction requires a constrained set of values.
 */
export function CellDropdownField(_ref) {
  var options = _ref.options,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$value = _ref.value,
    propsValue = _ref$value === void 0 ? '' : _ref$value,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? EMPTY_FUNCTION : _ref$onChange,
    _ref$onCancel = _ref.onCancel,
    onCancel = _ref$onCancel === void 0 ? EMPTY_FUNCTION : _ref$onCancel,
    _ref$onDone = _ref.onDone,
    onDone = _ref$onDone === void 0 ? EMPTY_FUNCTION : _ref$onDone,
    style = _ref.style;
  var _useState = useState(propsValue),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  // Use a ref for `isCancelled` so the focus change handler always sees the latest value
  var isCancelled = useRef(false);
  // Track whether a selection was committed so onOpenChange can distinguish ESC from a selection
  var isSelectionCommitted = useRef(false);
  // Direction to use when committing — set by capturing Enter before Spectrum consumes it
  var commitDirection = useRef(null);

  /**
   * Handle when the selected value changes in the Picker. Updates local state and calls onChange and onDone with the new value.
   * @param key the key of the newly selected item, or null if no item is selected
   */
  var handleChange = useCallback(key => {
    var _key$toString;
    var newValue = (_key$toString = key === null || key === void 0 ? void 0 : key.toString()) !== null && _key$toString !== void 0 ? _key$toString : '';
    isSelectionCommitted.current = true;
    setValue(newValue);
    onChange(newValue);
    onDone(newValue, {
      direction: commitDirection.current
    });
    commitDirection.current = null;
  }, [onChange, onDone]);

  /**
   * Capture Enter before Spectrum consumes it to record the intended commit direction.
   * Spectrum handles Enter to select the highlighted item and fires onChange — by the
   * time onChange fires the key event is gone, so we must capture direction here.
   */
  var handleWrapperKeyDownCapture = useCallback(event => {
    if (event.key === 'Enter') {
      commitDirection.current = event.shiftKey ? SELECTION_DIRECTION.UP : SELECTION_DIRECTION.DOWN;
    }
  }, []);

  /**
   * Handle key down events on the Picker. Currently only handles Escape to cancel the edit, but could be expanded to handle committing the edit with Enter or other keys.
   * @param event the keyboard event
   */
  var handleKeyDown = useCallback(event => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      isCancelled.current = true;
      onCancel();
    }
  }, [onCancel]);

  /**
   * When the overlay closes without a committed selection (e.g. ESC pressed while
   * the menu is open), cancel the edit. Spectrum consumes the ESC keydown to close
   * the overlay so onKeyDown never fires in that case.
   */
  var handleOpenChange = useCallback(isOpen => {
    if (!isOpen && !isSelectionCommitted.current) {
      if (commitDirection.current !== null) {
        // Enter was pressed on the already-selected item — Spectrum didn't fire
        // onChange because the value didn't change, but we should still commit.
        isSelectionCommitted.current = true;
        onDone(value, {
          direction: commitDirection.current
        });
        commitDirection.current = null;
      } else {
        isCancelled.current = true;
        onCancel();
      }
    }
  }, [onCancel, onDone, value]);

  /**
   * Commit the current value when the Picker loses focus entirely (trigger + overlay).
   * Skipped if the edit was already cancelled via Escape.
   */
  var handleFocusChange = useCallback(isFocused => {
    if (!isFocused && !isCancelled.current) {
      onDone(value, {
        direction: null
      });
    }
  }, [onDone, value]);
  return (
    /*#__PURE__*/
    // onKeyDownCapture captures Enter before Spectrum consumes it to set commit direction
    _jsx("div", {
      className: classNames('grid-cell-input-field', 'cell-dropdown-field', className),
      style: style,
      onKeyDownCapture: handleWrapperKeyDownCapture,
      children: /*#__PURE__*/_jsx(Picker, {
        UNSAFE_className: "cell-dropdown-field-picker",
        width: "100%",
        selectedKey: value || null,
        autoFocus: true,
        defaultOpen: true,
        onChange: handleChange,
        onKeyDown: handleKeyDown,
        onOpenChange: handleOpenChange,
        onFocusChange: handleFocusChange,
        "aria-label": "Cell value",
        isDisabled: disabled,
        isQuiet: true,
        children: options.map(option => /*#__PURE__*/_jsx(Item, {
          children: option
        }, option))
      })
    })
  );
}
export default CellDropdownField;
//# sourceMappingURL=CellDropdownField.js.map